/**
 * AiChat Component
 * Extracted from App.jsx - Phase 1 Refactoring
 */
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import MuiButton from "@mui/material/Button";
import MuiIconButton from "@mui/material/IconButton";
import { FONT } from "../theme/tokens";
import { useAppTheme } from "../theme/ThemeContext";
import { api, v } from "../utils/helpers";
import { Btn } from "../components/ui";

const AiChat=({spaces,prefillMessage,onPrefillConsumed})=>{
  const{theme}=useAppTheme();
  const{t,i18n}=useTranslation(["ki","gemeinsam"]);
  const gebiet=i18n.language==="en"?"en-GB":"de-DE";
  const[chats,setChats]=useState([]);const[activeChat,setActiveChat]=useState(null);
  const[messages,setMessages]=useState([]);const[input,setInput]=useState("");const[loading,setLoading]=useState(false);
  const[contextMode,setContextMode]=useState("general");
  const[selectedInst,setSelectedInst]=useState("");
  const[instances,setInstances]=useState([]);
  const bottomRef=useRef(null);const chatScrollRef=useRef(null);const isNearBottom=useRef(true);
  // Track if user is near bottom
  const onChatScroll=()=>{
    const el=chatScrollRef.current;if(!el)return;
    isNearBottom.current=el.scrollHeight-el.scrollTop-el.clientHeight<100;
  };
  // Load instances
  useEffect(()=>{
    api("/api/ai-enabled-instances").then(setInstances).catch(()=>setInstances([]));
  },[]);
  // Load chat history
  useEffect(()=>{
    api("/api/chat-history").then(setChats).catch(()=>{});
  },[]);
  useEffect(()=>{if(isNearBottom.current)bottomRef.current?.scrollIntoView({behavior:"smooth"});},[messages]);
  useEffect(()=>{if(prefillMessage){setInput(prefillMessage);onPrefillConsumed?.();}},[prefillMessage]);
  const newChat=()=>{setActiveChat(null);setMessages([]);};
  const loadChat=(chat)=>{setActiveChat(chat.id);setMessages(chat.messages||[]);};
  const saveChat=async(msgs)=>{
    const title=msgs.find(m=>m.role==="user")?.content?.substring(0,50)||"Neuer Chat";
    try{
      if(activeChat){
        await api(`/api/chat-history/${activeChat}`,{method:"PUT",body:JSON.stringify({messages:msgs,title})});
        setChats(p=>p.map(c=>c.id===activeChat?{...c,messages:msgs,title,updatedAt:new Date().toISOString()}:c));
      }else{
        const res=await api("/api/chat-history",{method:"POST",body:JSON.stringify({messages:msgs,title})});
        setActiveChat(res.id);setChats(p=>[res,...p]);
      }
    }catch{}
  };
  const deleteChat=async(id)=>{try{await api(`/api/chat-history/${id}`,{method:"DELETE"});setChats(p=>p.filter(c=>c.id!==id));if(activeChat===id)newChat();}catch{}};
  const send=async()=>{
    if(!input.trim()||loading)return;
    const userMsg={role:"user",content:input};const newMsgs=[...messages,userMsg];
    setMessages(newMsgs);setInput("");setLoading(true);
    try{
      const body={messages:newMsgs,iflowContext:{}};
      if(contextMode==="instance"&&selectedInst) body.iflowContext.instanceId=selectedInst;
      const res=await api("/api/chat",{method:"POST",body:JSON.stringify(body)});
      const allMsgs=[...newMsgs,{role:"assistant",content:res.response}];
      setMessages(allMsgs);saveChat(allMsgs);
    }catch(e){const allMsgs=[...newMsgs,{role:"assistant",content:t("chat.fehler",{meldung:e.message})}];setMessages(allMsgs);}
    setLoading(false);
  };
  return(
    <div style={{animation:"fadeIn 0.3s ease",display:"flex",height:"calc(100vh - 140px)",gap:0}}>
      {/* Sidebar */}
      <div style={{width:260,flexShrink:0,borderRight:`1px solid ${v("border")}`,display:"flex",flexDirection:"column",background:v("surface-0")}}>
        <div style={{padding:"16px 14px 12px",borderBottom:`1px solid ${v("border")}`}}>
          <MuiButton variant="outlined" fullWidth onClick={newChat} sx={{borderStyle:"dashed"}}>{t("chat.neu")}</MuiButton>
        </div>
        <div style={{flex:1,overflow:"auto",padding:6}}>
          {chats.length===0?<div style={{padding:20,textAlign:"center",color:v("text-3"),fontSize:12}}>{t("chat.keine")}</div>:
          chats.map(c=>(
            <div key={c.id} onClick={()=>loadChat(c)} style={{padding:"0 12px",borderRadius:8,marginBottom:2,cursor:"pointer",background:activeChat===c.id?v("surface-2"):"transparent",display:"flex",alignItems:"center",gap:8,transition:"background .1s"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:500,color:activeChat===c.id?v("text-1"):v("text-2"),overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.title||t("chat.ohneTitel")}</div>
                <div style={{fontSize:10,color:v("text-3"),marginTop:2}}>{c.updatedAt?new Date(c.updatedAt).toLocaleDateString(gebiet):""}</div>
              </div>
              <MuiIconButton size="small" onClick={e=>{e.stopPropagation();deleteChat(c.id);}} sx={{color:"text.secondary",flexShrink:0}}>×</MuiIconButton>
            </div>
          ))}
        </div>
      </div>
      {/* Chat Area */}
      <div style={{flex:1,display:"flex",flexDirection:"column",padding:"0 0 0 0"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 20px",borderBottom:`1px solid ${v("border")}`}}>
          <span style={{fontWeight:700,fontSize:15,color:v("text-1")}}>{t("chat.titel")}</span>
          <select value={contextMode} onChange={e=>setContextMode(e.target.value)} style={{background:v("surface-2"),border:`1px solid ${v("border")}`,borderRadius:6,padding:"5px 10px",color:v("text-1"),fontSize:11,fontFamily:FONT,outline:"none"}}>
            <option value="general">{t("chat.kontext.allgemein")}</option><option value="instance">{t("chat.kontext.instanz")}</option>
          </select>
          {contextMode==="instance"&&<select value={selectedInst} onChange={e=>setSelectedInst(e.target.value)} style={{background:v("surface-2"),border:`1px solid ${v("border")}`,borderRadius:6,padding:"5px 10px",color:v("text-1"),fontSize:11,fontFamily:FONT,outline:"none"}}>
            <option value="">{t("chat.instanzWaehlen")}</option>
            {instances.length===0
              ? <option value="" disabled>{t("chat.keineInstanzen")}</option>
              : instances.map(i=><option key={i.id} value={i.id}>{i.spaceName} / {i.name}</option>)
            }
          </select>}
        </div>
        <div ref={chatScrollRef} onScroll={onChatScroll} style={{flex:1,overflow:"auto",padding:"16px 20px"}}>
          {messages.length===0&&<div style={{textAlign:"center",paddingTop:80,color:v("text-3")}}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{marginBottom:12,opacity:.5}}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            <div style={{fontWeight:600,fontSize:16,color:v("text-2"),marginBottom:4}}>{t("chat.leer.titel")}</div>
            <div style={{fontSize:13}}>{t("chat.leer.text")}</div>
          </div>}
          {messages.map((m,i)=><div key={i} style={{marginBottom:14,display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
            <div style={{maxWidth:"70%",padding:"0 12px",borderRadius:m.role==="user"?"12px 12px 2px 12px":"12px 12px 12px 2px",background:m.role==="user"?(theme==="light"?"#1B2A4A":v("accent")):(theme==="light"?"#fff":v("surface-2")),color:m.role==="user"?"#fff":v("text-1"),fontSize:13,lineHeight:1.7,whiteSpace:"pre-wrap",border:m.role==="assistant"&&theme==="light"?`1px solid ${v("border")}`:"none"}}>{m.content}</div>
          </div>)}
          {loading&&<div style={{display:"flex",gap:4,padding:8}}><span style={{width:6,height:6,borderRadius:"50%",background:v("text-3"),animation:"pulse 1s infinite"}}/>
            <span style={{width:6,height:6,borderRadius:"50%",background:v("text-3"),animation:"pulse 1s infinite .2s"}}/>
            <span style={{width:6,height:6,borderRadius:"50%",background:v("text-3"),animation:"pulse 1s infinite .4s"}}/></div>}
          <div ref={bottomRef}/>
        </div>
        <div style={{padding:"12px 20px",borderTop:`1px solid ${v("border")}`,display:"flex",gap:8}}>
          <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey)send();}} placeholder={t("chat.eingabe")} style={{flex:1,background:v("surface-2"),border:`1px solid ${v("border")}`,borderRadius:8,padding:"0 12px",color:v("text-1"),fontSize:13,fontFamily:FONT,outline:"none"}}/>
          <Btn primary onClick={send} disabled={loading||!input.trim()} style={{padding:"11px 20px",bgcolor:theme==="light"?"#1B2A4A":undefined,"&:hover":{bgcolor:theme==="light"?"#253A5C":undefined}}}>{t("chat.senden")}</Btn>
        </div>
      </div>
    </div>
  );
};


export default AiChat;
